/**
 * Permission helpers for the client panel (TZ 8.1, FR-RB-01: a disabled section is hidden from the menu; the
 * API also answers 403). Store users only see their own stores and never the ranking of others (05 roles).
 */
import type { MeResponse, MetricKey, PermissionKey } from '../api/api_types';
import type { ControlPermissionKey } from '../api/control_types';

export type SectionKey =
  | 'dashboard'
  | 'stores'
  | 'comparison'
  | 'ranking'
  | 'trends'
  | 'trading_hours'
  | 'quality'
  | 'reports'
  | 'alerts'
  | 'store_control'
  | 'marketing_overview'
  | 'marketing_report'
  | 'marketing_campaigns'
  | 'marketing_content'
  | 'marketing_ads'
  | 'marketing_automation'
  | 'marketing_chats'
  | 'marketing_contacts'
  | 'marketing_sales'
  | 'marketing_connections'
  | 'settings';

export function hasPermission(session: MeResponse, permissionKey: PermissionKey): boolean {
  return session.permissions[permissionKey];
}

/** Comparison and ranking make sense only with at least two visible stores. */
export function canCompareStores(session: MeResponse): boolean {
  return hasPermission(session, 'compare_ranking') && session.stores.length >= 2;
}

/**
 * Store control flags (docs/plans/store_control_v1.md, dotted names as the API sends them). The super-admin turns
 * them on only for a company that has store control, so every existing company keeps the section hidden.
 */
export function hasControlPermission(session: MeResponse, permissionKey: ControlPermissionKey): boolean {
  return session.permissions[permissionKey];
}

/** The "Do'kon nazorati" section, dashboard card and store block: company has store control + control.view. */
export function hasStoreControl(session: MeResponse): boolean {
  return hasControlPermission(session, 'control.view');
}

export function isSectionVisible(session: MeResponse, sectionKey: SectionKey): boolean {
  switch (sectionKey) {
    case 'dashboard':
    case 'stores':
    case 'trends':
    case 'settings':
      return hasPermission(session, 'visits');
    // Budgets, sales and account connections belong to the company, not to one store's staff.
    case 'marketing_overview':
    case 'marketing_report':
    case 'marketing_campaigns':
    case 'marketing_content':
    case 'marketing_ads':
    case 'marketing_automation':
    case 'marketing_chats':
    case 'marketing_contacts':
    case 'marketing_sales':
    case 'marketing_connections':
      return hasPermission(session, 'visits') && session.user.role === 'company_admin';
    case 'comparison':
    case 'ranking':
      return canCompareStores(session);
    case 'trading_hours':
      return hasPermission(session, 'work_hours');
    case 'quality':
      return hasPermission(session, 'quality');
    case 'reports':
      return hasPermission(session, 'export');
    case 'alerts':
      return hasPermission(session, 'scheduled_reports_alerts');
    case 'store_control':
      return hasStoreControl(session);
  }
}

const METRIC_PERMISSIONS: Record<MetricKey, PermissionKey> = {
  visits: 'visits',
  unique: 'unique_returning',
  returning: 'unique_returning',
  children: 'children',
  cancelled: 'cancelled',
  staff: 'staff',
  work_hours: 'work_hours',
  visits_per_hour: 'work_hours',
  coverage: 'quality',
};

export function isMetricVisible(session: MeResponse, metricKey: MetricKey): boolean {
  return hasPermission(session, METRIC_PERMISSIONS[metricKey]);
}

export function visibleMetrics(session: MeResponse, metricKeys: readonly MetricKey[]): MetricKey[] {
  return metricKeys.filter((metricKey) => isMetricVisible(session, metricKey));
}

/**
 * The single "how many people came" number of a store or the network (owner decision 2026-10-05: like the legacy
 * reports, unique visitors "Noyob", not total entries). Falls back to visits when the company does not show unique.
 */
export function primaryVisitorMetric(session: MeResponse): 'unique' | 'visits' {
  return isMetricVisible(session, 'unique') ? 'unique' : 'visits';
}
